import { useState, useEffect, useRef } from "react";
import Delete from "@mui/icons-material/DeleteTwoTone";

const CanvasDraw = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPosition, setLastPosition] = useState(false);
  const [lineWidth, setLineWidth] = useState(5);
  const [lineColor, setLineColor] = useState("#000000");
  const [hasDrawn, setHasDrawn] = useState(false);
  const [isMousePressed, setIsMousePressed] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    if (!hasDrawn) {
      ctx.font = "20px Arial";
      ctx.fillStyle = "gray";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("Draw here", canvas.width / 2, canvas.height / 2);
    }
  }, [hasDrawn]); // Re-run when `hasDrawn` changes

  const getMouseOrTouchPosition = event => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    if (event.touches) {
      // Touch event
      const touch = event.touches[0];
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    } else {
      // Mouse event
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    }
  };

  const handleSingleClick = event => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Get the mouse position relative to the canvas
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Draw a single mark (circle) at the clicked position
    ctx.beginPath();
    ctx.arc(x, y, lineWidth / 2, 0, 2 * Math.PI);
    ctx.fillStyle = lineColor;
    ctx.fill();
    ctx.closePath();
  };

  const startDrawing = event => {
    setIsDrawing(true);
    setIsMousePressed(true);
    handleSingleClick(event); // Handle single click immediately
    const canvas = canvasRef.current;

    // Clear the "Draw here" text
    if (!hasDrawn) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasDrawn(true);
    }

    // Get & set the initial mouse position
    const { x, y } = getMouseOrTouchPosition(event);
    setLastPosition({ x, y });
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    setIsMousePressed(false);
    setLastPosition(false); // Reset the last position
  };

  const handleMouseEnter = event => {
    // If mouse was pressed outside and now enters canvas, resume drawing
    if (isMousePressed && !isDrawing) {
      setIsDrawing(true);
      // Get current position and set as last position to continue drawing
      const { x, y } = getMouseOrTouchPosition(event);
      setLastPosition({ x, y });
    }
  };

  const handleMouseLeave = () => {
    // Only stop drawing if mouse is not pressed, otherwise just pause
    if (isMousePressed) {
      // Pause drawing but keep mouse pressed state
      setIsDrawing(false);
    } else {
      stopDrawing();
    }
  };

  const draw = event => {
    if (!isDrawing) return; // Only draw if the mouse is pressed

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const { x, y } = getMouseOrTouchPosition(event);

    if (lastPosition) {
      // Draw a line from the last position to the current position
      ctx.beginPath();
      ctx.moveTo(lastPosition.x, lastPosition.y); // Start at the last position
      ctx.lineTo(x, y); // Draw to the current position
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.closePath();
    }

    setLastPosition({ x, y });
  };

  const handleLineWidthChange = event => {
    setLineWidth(event.target.value);
  };

  const handleLineColorChange = event => {
    setLineColor(event.target.value);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        id="canvas"
        width="300"
        height="300"
        style={{ cursor: "pointer" }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      ></canvas>
      <div style={{ position: "absolute", top: "20px" }}>
        <label htmlFor="lineWidthSlider" style={{ position: "relative", top: "4px", margin: "0 5px 0 35px" }}>
          {lineWidth}
        </label>
        <input
          id="lineWidthSlider"
          type="range"
          min="1"
          max="9"
          value={lineWidth}
          onChange={handleLineWidthChange}
          style={{ width: "100px" }}
        />
        <input
          id="lineColorPicker"
          type="color"
          value={lineColor}
          onChange={handleLineColorChange}
          style={{ marginLeft: "20px" }}
        />
        <Delete
          style={{
            position: "relative",
            top: "3px",
            marginLeft: "20px",
            cursor: "pointer",
          }}
          onClick={() => {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear the entire canvas
            // setHasDrawn(false); // Reset the "Draw here" text
          }}
        />
      </div>
    </div>
  );
};

export default CanvasDraw;
